// Combat resolution. Faithful quirks kept from the original prototype:
//  - trySpendStamina drains even when it can't afford the full amount (the
//    failed action still empties the bar and can push you into exhaustion).
//  - A guard-broken block takes FULL damage plus the long stun.
//  - An unblocked hit cancels an active block (e.g. hit from behind).
// New read-game layer: parry (fresh block) staggers the attacker, kick crushes
// any held guard, dodge i-frames avoid everything.

import {
  DODGE_IFRAME_END,
  DODGE_IFRAME_START,
  HEAVY_BLOCK_STAMINA_MULTIPLIER,
  KICK_GUARD_CRUSH_STAMINA,
  PARRIED_STUN,
  PARRY_WINDOW,
} from './attacks';
import type { AttackKind } from './attacks';
import { TICK_RATE } from './constants';
import { PROFILES } from './profiles';
import type { CharacterState, HitOutcome, SimEvent } from './types';

const EPSILON = 1e-6;

/** CombatMath-style horizontal arc test on the XZ plane. */
export function isInsideArc(
  heading: number,
  dirX: number,
  dirZ: number,
  fullArcDegrees: number
): boolean {
  const len = Math.hypot(dirX, dirZ);
  if (len <= EPSILON) return true;
  const minimumDot = Math.cos((Math.min(Math.max(fullArcDegrees, 0), 360) / 2) * (Math.PI / 180));
  const dot = (Math.sin(heading) * dirX + Math.cos(heading) * dirZ) / len;
  return dot + EPSILON >= minimumDot;
}

/** Mutates the character. Lenient rule: any stamina at all lets the action
 * through (the bar just empties); only a truly empty/exhausted bar refuses.
 * The old "failed spend still drains you into exhaustion" rule felt terrible. */
export function trySpendStamina(c: CharacterState, amount: number): boolean {
  if (amount <= 0) return !c.exhausted;
  if (c.exhausted || c.stamina <= EPSILON) return false;
  c.stamina = Math.max(0, c.stamina - amount);
  c.regenDelay = PROFILES[c.profile].staminaRegenerationDelay;
  if (c.stamina <= EPSILON) c.exhausted = true;
  return true;
}

/** Stamina regeneration with delay and exhaustion recovery threshold. */
export function tickStamina(c: CharacterState, dt: number): void {
  const p = PROFILES[c.profile];
  if (c.stamina >= p.maximumStamina) return;
  if (c.regenDelay > 0) {
    c.regenDelay = Math.max(0, c.regenDelay - dt);
    return;
  }
  c.stamina = Math.min(p.maximumStamina, c.stamina + p.staminaRegenerationPerSecond * dt);
  if (c.exhausted && c.stamina + EPSILON >= p.maximumStamina * p.exhaustedRecoveryFraction) {
    c.exhausted = false;
  }
}

export function isInDodgeIFrames(c: CharacterState): boolean {
  return c.dodging && c.dodgeElapsed >= DODGE_IFRAME_START && c.dodgeElapsed <= DODGE_IFRAME_END;
}

/** Resolve a melee hit on the defender. Mutates defender (and, on a parry, the
 * attacker). Returns the outcome. */
export function receiveHit(
  defender: CharacterState,
  attacker: CharacterState,
  kind: AttackKind,
  damage: number,
  tick: number,
  events?: SimEvent[]
): HitOutcome {
  const p = PROFILES[defender.profile];

  if (isInDodgeIFrames(defender)) return 'dodged';

  const dirX = attacker.x - defender.x;
  const dirZ = attacker.z - defender.z;
  const frontal = defender.blocking && isInsideArc(defender.heading, dirX, dirZ, p.blockArc);

  let outcome: HitOutcome;
  let applied = Math.max(0, damage);

  if (kind === 'kick') {
    if (frontal) {
      // guard crush: block torn open, stamina ripped, long stagger
      outcome = 'guardCrushed';
      defender.blocking = false;
      defender.blockHeldTicks = 0;
      defender.stagger = Math.max(defender.stagger, p.guardBreakStunDuration);
      trySpendStamina(defender, KICK_GUARD_CRUSH_STAMINA);
    } else {
      // a kick on a non-blocker is nearly harmless — that's the gamble
      outcome = 'unblocked';
      defender.stagger = Math.max(defender.stagger, 0.08);
    }
  } else if (frontal && defender.blockHeldTicks <= PARRY_WINDOW * TICK_RATE) {
    // parry: a freshly-raised block turns the hit back on the attacker
    attacker.attacking = false;
    attacker.attackKind = null;
    attacker.attackElapsed = 0;
    attacker.attackResolved = false;
    attacker.stagger = Math.max(attacker.stagger, PARRIED_STUN);
    attacker.lastHitTick = tick;
    attacker.lastHitOutcome = 'parried';
    events?.push({ type: 'parry', x: defender.x, z: defender.z });
    return 'parried';
  } else if (frontal) {
    const staminaCost =
      damage * p.blockStaminaCostPerDamage * (kind === 'heavy' ? HEAVY_BLOCK_STAMINA_MULTIPLIER : 1);
    if (!trySpendStamina(defender, staminaCost)) {
      // blocking on an empty bar: full guard break, full damage
      outcome = 'guardBroken';
      defender.blocking = false;
      defender.blockHeldTicks = 0;
      defender.stagger = Math.max(defender.stagger, p.guardBreakStunDuration);
    } else if (defender.exhausted) {
      // this block just emptied the bar: guard cracks, but the hit was absorbed
      outcome = 'guardBroken';
      applied *= Math.min(1, Math.max(0, p.blockedDamageMultiplier));
      defender.blocking = false;
      defender.blockHeldTicks = 0;
      defender.stagger = Math.max(defender.stagger, p.guardBreakStunDuration);
    } else {
      outcome = 'blocked';
      applied *= Math.min(1, Math.max(0, p.blockedDamageMultiplier));
      defender.stagger = Math.max(defender.stagger, p.blockedHitStunDuration);
    }
  } else {
    outcome = 'unblocked';
    defender.blocking = false;
    defender.blockHeldTicks = 0;
    defender.stagger = Math.max(defender.stagger, p.hitStunDuration);
  }

  defender.health = Math.max(0, defender.health - applied);
  defender.lastHitTick = tick;
  defender.lastHitOutcome = outcome;
  defender.morale = Math.max(0, defender.morale - (outcome === 'unblocked' ? 0.05 : 0.02));
  if (events) {
    const type =
      outcome === 'unblocked'
        ? 'hit'
        : outcome === 'blocked'
          ? 'blocked'
          : 'guard-break'; // guardBroken and guardCrushed share a sound
    events.push({ type, x: defender.x, z: defender.z });
  }
  if (defender.health <= 0) {
    defender.alive = false;
    resetCombatState(defender);
    events?.push({ type: 'death', x: defender.x, z: defender.z, team: defender.team });
  }
  return outcome;
}

/** Arrow impact: blockable (half stamina cost, no parry, no guard break),
 * dodgeable, lighter stun than melee. */
export function receiveArrowHit(
  defender: CharacterState,
  damage: number,
  fromX: number,
  fromZ: number,
  tick: number,
  events?: SimEvent[]
): HitOutcome {
  if (isInDodgeIFrames(defender)) return 'dodged';
  const p = PROFILES[defender.profile];
  const frontal =
    defender.blocking &&
    isInsideArc(defender.heading, fromX - defender.x, fromZ - defender.z, p.blockArc);

  let outcome: HitOutcome;
  let applied = Math.max(0, damage);
  if (frontal && trySpendStamina(defender, damage * p.blockStaminaCostPerDamage * 0.5)) {
    outcome = 'blocked';
    applied *= Math.min(1, Math.max(0, p.blockedDamageMultiplier));
    defender.stagger = Math.max(defender.stagger, p.blockedHitStunDuration * 0.5);
  } else {
    outcome = 'unblocked';
    defender.stagger = Math.max(defender.stagger, p.hitStunDuration * 0.6);
  }

  defender.health = Math.max(0, defender.health - applied);
  defender.lastHitTick = tick;
  defender.lastHitOutcome = outcome;
  defender.morale = Math.max(0, defender.morale - (outcome === 'unblocked' ? 0.05 : 0.02));
  events?.push({ type: outcome === 'blocked' ? 'blocked' : 'hit', x: defender.x, z: defender.z });
  if (defender.health <= 0) {
    defender.alive = false;
    resetCombatState(defender);
    events?.push({ type: 'death', x: defender.x, z: defender.z, team: defender.team });
  }
  return outcome;
}

export function resetCombatState(c: CharacterState): void {
  c.attacking = false;
  c.attackKind = null;
  c.attackElapsed = 0;
  c.attackResolved = false;
  c.blocking = false;
  c.blockHeldTicks = 0;
  c.stagger = 0;
  c.dodging = false;
  c.dodgeElapsed = 0;
}

export function canSprint(c: CharacterState): boolean {
  return c.alive && !c.attacking && !c.blocking && !c.dodging && c.stagger <= 0 && !c.exhausted;
}
